// Copyright 2023 Sorama B.V.

import { DialogService } from "aurelia-dialog";
import { autoinject, bindable, computedFrom, observable, PLATFORM, transient } from "aurelia-framework";
import {
    ActivatedFeatureLicenseDTO,
    ActivatedLicenseDTO,
    LicenseBundle,
    LicenseKind,
    SellableLicense,
} from "datatypes/SoramaWebApi";
import { FileDownloader } from "libraries/FileDownloader/FileDownloader";
import { ProgressIndicator } from "libraries/ProgressIndicator/ProgressIndicator";
import {
    IWorkflowModuleInstance,
    IWorkflowModuleLifetime,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { WorkflowPropertyBindingMode } from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { workflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyAttribute";
import { ConfirmationModel } from "modules/dialogs/Confirmation/ConfirmationDialog";
import { ErrorDialogModel } from "modules/dialogs/Error/ErrorDialog";
import { RecoverSoftwarePackagesModel } from "modules/dialogs/RecoverSoftwarePackages/RecoverSoftwarePackagesDialog";
import { ShellViewManager } from "modules/shell/App/ShellViewManager";
import { FirmwareRepository } from "repositories/FirmwareRepository";
import { LicenseBundleRepository } from "repositories/LicenseBundleRepository";
import { CamIv64Device, CamIV64Helpers } from "services/Hardware/CamHTTP/CamIV64Helpers";
import { CamIV64Service } from "services/Hardware/CamHTTP/CamIV64Service";
import { FirmwareInfo, ICamApi } from "services/Hardware/CamHTTP/ICamApi";
import { ILicenseService } from "services/License/ILicenseService";
import { WorkflowModeService } from "services/WorkflowModes/WorkflowModeService";
import { BinaryData } from "units/Data";

/**
 * MODULE OPERATION DESCRIPTION - Device Management Module
 * The tab where you can manage your selected device, activate features, do firmware updates, factory reset, log files, etc.
 */
enum ResetState {
    Init = "init",
    Resetting = "resetting",
    Succeeded = "succeeded",
    Failed = "failed",
}

enum FirmwareUpdateState {
    Disabled = "disabled",
    Init = "init",
    Downloading = "downloading",
    Uploading = "uploading",
    Succeeded = "succeeded",
    Failed = "failed",
}

type BundleWithActivatableFeatures = LicenseBundle & {
    activatableFeatures?: LicenseKind[];
};

@transient()
@autoinject()
export class DeviceManagementModule implements IWorkflowModuleLifetime {
    @bindable
    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.oneWay })
    public frontEndId: string;
    private moduleInstance: IWorkflowModuleInstance;
    private busyLoadingDevice = true;
    private frontEnd: CamIv64Device;
    // #region Firmware-related properties

    protected allowLocalFile = false;
    protected allowLocalFileTime = new Date().getTime();

    @observable
    protected isFirmwareUpdateCollapsed = false;

    private firmwareFileUpload: FileList;

    private installedVersion: FirmwareInfo;
    private remoteVersion: string;
    private updateState = FirmwareUpdateState.Init;
    private updateErrorMessage: string;
    private progressDownloadedMB = 0;
    private progressUploadedMB = 0;
    private totalMB = 0;
    private downloadProgressBar = 0;
    private uploadProgressBar = 0;
    private placeHolderDownloadSize = 600;

    // #endregion

    private resetState = ResetState.Init;

    private installedLicenses: ActivatedLicenseDTO[] = [];
    private missingLicenses: ActivatedLicenseDTO[] = [];
    private missingBundles: LicenseBundle[] = [];

    private ownedBundles: BundleWithActivatableFeatures[] = [];
    @computedFrom("ownedBundles")
    private get installableBundles(): BundleWithActivatableFeatures[] {
        return this.ownedBundles.filter((b) => !b.DeviceId);
    }
    @computedFrom("ownedBundles")
    private get installedBundles(): BundleWithActivatableFeatures[] {
        return this.ownedBundles.filter((b) => b.DeviceId === this.deviceId);
    }

    private get bonusFeatures(): LicenseKind[] {
        const allBonusFeatures = this.installedBundles.reduce((acc, b) => [...acc, ...b.activatableFeatures], []);
        // Remove duplicates based on feature tag
        const bonusFeatureSet = [];
        allBonusFeatures.forEach((f) => {
            if (!bonusFeatureSet.find((x) => x.LicenseKindTag === f.LicenseKindTag)) {
                bonusFeatureSet.push(f);
            }
        });
        return bonusFeatureSet;
    }

    @computedFrom("frontEnd")
    private get deviceId(): string {
        return this.frontEnd?.id ?? "";
    }

    @computedFrom("frontEnd", "workflowModeService.selectedWorkflowMode")
    private get isDeviceCompatible(): boolean {
        if (!this.frontEnd) return false;

        const deviceType = CamIV64Helpers.getDeviceType(this.frontEnd.id);
        if (!deviceType) return false;

        const supportedCamTypes = this.workflowModeService.selectedWorkflowMode.CAMSet;

        switch (deviceType) {
            case "iV64":
                return supportedCamTypes.has("CAMiV64");
            case "iV64Ex":
                return supportedCamTypes.has("CAMiV64Ex");
            case "iV64s":
                return supportedCamTypes.has("CAMiV64s");
            default:
                return false;
        }
    }

    @computedFrom("frontEnd")
    private get deviceTypeName(): string {
        if (!this.frontEnd) return "Unknown";
        const deviceType = CamIV64Helpers.getDeviceType(this.frontEnd.id);
        return deviceType ? `CAM ${deviceType}` : "Unknown Device";
    }

    //#region Lifecycle and Initialization

    constructor(
        private readonly shellViewManager: ShellViewManager,
        private readonly camService: CamIV64Service,
        private readonly camApi: ICamApi,
        private readonly firmwareRepository: FirmwareRepository,
        private readonly licenseBundleRepository: LicenseBundleRepository,
        private readonly licenseService: ILicenseService,
        private readonly dialogService: DialogService,
        private readonly workflowModeService: WorkflowModeService
    ) {}

    public async activateModule(moduleInstance: IWorkflowModuleInstance): Promise<void> {
        await this.loadFrontEnd();
        this.loadFirmware();
        this.loadBundles();
        this.loadInstalledLicenses();
        this.installedVersion = await this.camApi.getDeviceFirmware();
        this.moduleInstance = moduleInstance;
    }

    private async loadFirmware(): Promise<void> {
        try {
            this.remoteVersion = await this.firmwareRepository.getLatestVersion(
                CamIV64Helpers.getDeviceType(this.deviceId)
            );
        } catch (e) {
            this.remoteVersion = undefined;
            console.error(
                "Could not fetch the latest " +
                    CamIV64Helpers.getDeviceType(this.deviceId) +
                    " firmware version: " +
                    e.message
            );
        }
    }

    private async loadFrontEnd(): Promise<void> {
        // No device was specified in the url, and we can't figure out what the user wants to select, so send them back
        try {
            this.frontEnd = await this.camService.getHardwareById(this.frontEndId);
            this.camService.selectedDeviceId = this.frontEndId;
        } catch (e) {
            // Ignore the error
        } finally {
            // If something went wrong, an alert to go back will show because we are not busy with no frontend
            this.busyLoadingDevice = false;
        }
    }

    private async loadBundles(): Promise<void> {
        this.ownedBundles = await this.licenseBundleRepository.getOwnedLicenseBundles();
        this.ownedBundles.forEach((b) => {
            b.activatableFeatures = [];
            this.licenseBundleRepository
                .getActivatableBundleFeaturesForDevice(b.Id, CamIV64Helpers.getSerialNumber(this.deviceId))
                .then((activatableFeatures) => {
                    b.activatableFeatures = activatableFeatures;
                });
        });
    }

    private async loadInstalledLicenses(): Promise<void> {
        let licensesOnDevice = [];

        // this.deviceId result "" when calling this method so for now it uses this.frontEndId
        const promises = [
            this.licenseService
                .getActivatedLicensesForDevice(CamIV64Helpers.getSerialNumber(this.frontEndId))
                .then((result) => {
                    const currentDate = new Date();
                    this.installedLicenses = result.filter((license: ActivatedLicenseDTO) => {
                        return currentDate < new Date(license.DateEnd);
                    });
                })
                .catch((err) => console.error(err)),
            this.camApi
                .getLicenseTagsOnDevice()
                .then((result) => (licensesOnDevice = result ?? []))
                .catch((err) => console.error(err)),
        ];

        await Promise.all(promises);

        // Filter based on what is not contained in licensesOnDevice
        this.missingLicenses = this.installedLicenses.filter((l) =>
            licensesOnDevice.every((il) => il !== l?.LicenseKind?.LicenseKindTag)
        );

        this.computeMissingBundles(licensesOnDevice);
    }

    private computeMissingBundles(licensesOnDevice: string[]): void {
        this.missingBundles = this.installedBundles.filter((b) => {
            const featureTags = b.LicenseBundleKind?.LicenseKinds?.map((lk) => lk.LicenseKindTag) ?? [];
            return featureTags.length > 0 && featureTags.every((tag) => !licensesOnDevice.includes(tag));
        });
    }

    public deactivateModule(): void {}

    //#endregion

    protected navigateBack(): void {
        this.shellViewManager.openRightSideBar("hardwareselection");
    }

    //#region Firmware update

    private async updateFirmware() {
        const firmwareFile = await this.downloadFirmware(this.remoteVersion);
        await this.uploadFirmware(firmwareFile);
    }

    private async updateFirmwareManual() {
        const firmwareFile = this.firmwareFileUpload.item(0);
        await this.uploadFirmware(firmwareFile);
    }

    private async downloadFirmware(version: string) {
        this.updateState = FirmwareUpdateState.Downloading;
        this.totalMB =
            (await this.firmwareRepository.getFirmwareSize(CamIV64Helpers.getDeviceType(this.deviceId), version)) /
            BinaryData.mebibyte.conversion;

        // Temporary fix when the download size cannot be retrieved, gives the user a rough time indication
        if (this.totalMB <= 0) this.totalMB = this.placeHolderDownloadSize;

        return this.firmwareRepository.downloadFirmware(
            CamIV64Helpers.getDeviceType(this.deviceId),
            this.remoteVersion,
            (progress: ProgressIndicator) => {
                this.progressDownloadedMB = progress.progressEvent.loaded / BinaryData.mebibyte.conversion;
                this.downloadProgressBar = this.progressDownloadedMB / this.totalMB;
            }
        );
    }

    private async uploadFirmware(firmwareFile: Blob) {
        this.updateState = FirmwareUpdateState.Uploading;
        this.updateErrorMessage = undefined;
        this.camApi
            .updateDeviceFirmware(firmwareFile, (progress: ProgressIndicator) => {
                this.progressUploadedMB = progress.progressEvent.loaded / BinaryData.mebibyte.conversion;
                this.totalMB = progress.progressEvent.total / BinaryData.mebibyte.conversion;
                this.uploadProgressBar = this.progressUploadedMB / this.totalMB;
            })
            .then(() => {
                this.updateState = FirmwareUpdateState.Succeeded;
            })
            .catch((e) => {
                this.updateState = FirmwareUpdateState.Failed;
                this.updateErrorMessage = e.message;
            });
    }

    //#endregion

    public async downloadLogs(): Promise<void> {
        const url = await this.camApi.getLogUrl();
        // Get extension from URL
        const extension = url.split(".").pop() || "log";
        FileDownloader.downloadAsFileFromURL(url, `${this.deviceId}_logs_${new Date().toISOString()}.${extension}`);
    }

    private factoryReset() {
        const confirmationModel: ConfirmationModel = {
            title: "Proceed with caution!",
            text: "Resetting will erase custom settings, remove installed bundles/licenses (can be restored via Sorama Portal), and may temporarily block access to recordings and special measurements.",
            confirmText: "Perform factory reset",
            operation: async () => {
                this.resetState = ResetState.Resetting;
                this.camApi
                    .performFactoryReset()
                    .then(() => {
                        this.resetState = ResetState.Succeeded;
                    })
                    .catch(() => {
                        this.resetState = ResetState.Failed;
                    });
            },
        };

        this.dialogService.open({
            viewModel: PLATFORM.moduleName("modules/dialogs/Confirmation/ConfirmationDialog"),
            model: confirmationModel,
            lock: true,
        });
    }

    //#region Feature license management

    private openFeatureStore(): void {
        this.shellViewManager.openRightSideBar("feature-store");
    }

    private async clickedFeatureLicense(license: SellableLicense): Promise<void> {
        // Show an "are you sure" dialog
        await this.dialogService
            .open({
                viewModel: PLATFORM.moduleName("modules/dialogs/Confirmation/ConfirmationDialog"),
                model: <ConfirmationModel>{
                    title: `Are you sure you want to activate the ${license.LicenseKind.LicenseKindName} license on this device?`,
                    text: "This action cannot be undone.",
                    confirmText: "Activate",
                    operation: () => this.activateLicense(license),
                },
                lock: true,
            })
            .whenClosed(async (result) => {
                if (result.wasCancelled) {
                    // An error occurred
                    if (result.output) {
                        await this.dialogService.open({
                            viewModel: PLATFORM.moduleName("modules/dialogs/Error/ErrorDialog"),
                            model: <ErrorDialogModel>{
                                text: result.output,
                            },
                        });
                    }
                } else {
                    // License activation successful, refresh license lists
                    await this.loadInstalledLicenses();
                }
                this.licenseService.reloadSellableFeatureLicenses();
            });
    }

    private async clickedMissingLicense(license: ActivatedLicenseDTO): Promise<void> {
        // Show an "are you sure" dialog
        await this.dialogService
            .open({
                viewModel: PLATFORM.moduleName("modules/dialogs/Confirmation/ConfirmationDialog"),
                model: <ConfirmationModel>{
                    title: `Are you sure you want to restore the ${license.LicenseKind.LicenseKindName} license on this device?`,
                    text: "This action cannot be undone.",
                    confirmText: "Restore and Activate",
                    operation: () => this.restoreLicense(license),
                },
                lock: true,
            })
            .whenClosed(async (result) => {
                if (result.wasCancelled) {
                    // An error occurred
                    if (result.output) {
                        await this.dialogService.open({
                            viewModel: PLATFORM.moduleName("modules/dialogs/Error/ErrorDialog"),
                            model: <ErrorDialogModel>{
                                text: result.output,
                            },
                        });
                    }
                } else {
                    await this.loadInstalledLicenses();
                }
            });
    }

    private async clickedMissingBundle(bundle: LicenseBundle): Promise<void> {
        const hardwareClock = await this.camApi.getHardwareClock();
        await this.dialogService
            .open({
                viewModel: PLATFORM.moduleName("modules/dialogs/RecoverSoftwarePackages/RecoverSoftwarePackagesDialog"),
                model: <RecoverSoftwarePackagesModel>{
                    device: new CamIv64Device(this.deviceId),
                    missingPackages: [bundle],
                    hardwareClock: hardwareClock,
                },
                lock: true,
            })
            .whenClosed(async (result) => {
                if (!result.wasCancelled) {
                    await this.loadBundles();
                    await this.loadInstalledLicenses();
                }
            });
    }

    private async clickedBundle(bundle: LicenseBundle): Promise<void> {
        // Show an "are you sure" dialog
        await this.dialogService
            .open({
                viewModel: PLATFORM.moduleName("modules/dialogs/Confirmation/ConfirmationDialog"),
                model: <ConfirmationModel>{
                    title: `Are you sure you want to activate the ${bundle.LicenseBundleKind.Name} package on this device?`,
                    confirmText: "Activate",
                    operation: () => this.activateBundle(bundle),
                },
                lock: true,
            })
            .whenClosed(async (result) => {
                if (result.wasCancelled) {
                    // An error occurred
                    if (result.output) {
                        await this.dialogService.open({
                            viewModel: PLATFORM.moduleName("modules/dialogs/Error/ErrorDialog"),
                            model: <ErrorDialogModel>{
                                text: result.output,
                            },
                        });
                    }
                } else {
                    // The bundle was successfully activated. Update the owned bundles and licenses to show success
                    await this.loadBundles();
                    await this.loadInstalledLicenses();
                    this.licenseService.reloadSellableFeatureLicenses();
                }
            });
    }

    private async clickedUpdateBundles(): Promise<void> {
        // Show an "are you sure" dialog
        await this.dialogService
            .open({
                viewModel: PLATFORM.moduleName("modules/dialogs/Confirmation/ConfirmationDialog"),
                model: <ConfirmationModel>{
                    title: `Are you sure you want to update the bundle${
                        this.installedBundles.length > 1 ? "s" : ""
                    } on this device?`,
                    confirmText: "Activate",
                    operation: () => this.updateBundles(),
                },
                lock: true,
            })
            .whenClosed(async (result) => {
                if (result.wasCancelled) {
                    // An error occurred
                    if (result.output) {
                        await this.dialogService.open({
                            viewModel: PLATFORM.moduleName("modules/dialogs/Error/ErrorDialog"),
                            model: <ErrorDialogModel>{
                                text: result.output,
                            },
                        });
                    }
                } else {
                    // Bundle update successful, refresh license lists
                    await this.loadInstalledLicenses();
                    await this.loadBundles();
                    this.licenseService.reloadSellableFeatureLicenses();
                }
            });
    }

    private async activateLicense(license: SellableLicense): Promise<void> {
        // Fetch time from device
        const hardwareClock = await this.getHardwareClock();

        // Get activated license from backend
        const encryptedLicense = await this.licenseService
            .activateFeature(license.Id, CamIV64Helpers.getSerialNumber(this.deviceId), hardwareClock)
            .catch((e) => {
                throw new Error("Could not activate the feature license: " + e.message);
            });

        // Post to device
        await this.camApi.sendLicenseToDevice(encryptedLicense);
    }

    private async restoreLicense(license: ActivatedLicenseDTO): Promise<void> {
        // Fetch time from device
        const hardwareClock = await this.getHardwareClock();

        // Get activated license from backend
        const encryptedLicense = await this.licenseService
            .encryptLicense(license.Id, CamIV64Helpers.getSerialNumber(this.deviceId), hardwareClock)
            .catch((e) => {
                throw new Error("Could not encrypt the feature license: " + e.message);
            });

        // Post to device
        await this.camApi.sendLicenseToDevice(encryptedLicense);
    }

    // This can throw
    private async activateBundle(bundle: BundleWithActivatableFeatures): Promise<void> {
        // Fetch time from device
        const hardwareClock = await this.camApi.getHardwareClock();

        // For an uninstalled bundle, activatableFeatures will contain the base set of features + any bonus licenses released since ordering it
        for (const licenseKind of bundle.activatableFeatures) {
            const licenseString = await this.licenseBundleRepository.activateBundleFeature(
                bundle.Id,
                licenseKind.LicenseKindTag,
                {
                    DeviceType: CamIV64Helpers.getDeviceType(this.deviceId),
                    DeviceId: CamIV64Helpers.getSerialNumber(this.deviceId),
                    HardwareClock: hardwareClock,
                } as ActivatedFeatureLicenseDTO
            );
            await this.camApi.sendLicenseToDevice(licenseString);
        }

        bundle.DeviceId = this.deviceId;
    }

    private async updateBundles(): Promise<void> {
        const hardwareClock = await this.camApi.getHardwareClock();

        // Make sure we don't activate the same bonus feature twice
        const installedBonusFeatures = new Set<string>();

        for (const bundle of this.installedBundles) {
            const activatableFeatures = bundle.activatableFeatures;
            for (const licenseKind of activatableFeatures) {
                // Skip if we already activated this bonus feature
                if (installedBonusFeatures.has(licenseKind.LicenseKindTag)) continue;

                const licenseString = await this.licenseBundleRepository.activateBundleFeature(
                    bundle.Id,
                    licenseKind.LicenseKindTag,
                    {
                        DeviceType: CamIV64Helpers.getDeviceType(this.deviceId),
                        DeviceId: CamIV64Helpers.getSerialNumber(this.deviceId),
                        HardwareClock: hardwareClock,
                    } as ActivatedFeatureLicenseDTO
                );
                await this.camApi.sendLicenseToDevice(licenseString);

                installedBonusFeatures.add(licenseKind.LicenseKindTag);

                bundle.activatableFeatures = bundle.activatableFeatures.filter(
                    (x) => x.LicenseKindTag !== licenseKind.LicenseKindTag
                );
            }
        }
    }

    private async getHardwareClock(): Promise<number> {
        const hardwareClock = await this.camApi.getHardwareClock();
        if (hardwareClock == undefined) {
            throw new Error("Could not fetch additionally needed device information.");
        }
        return hardwareClock;
    }

    private async restoreLicenses(deviceId: string): Promise<void> {
        const hardwareClock = await this.getHardwareClock();

        await this.licenseService
            .restoreFeatureLicenses(deviceId, hardwareClock)
            .then((licenses) => {
                licenses.forEach((license) => {
                    this.camApi.sendLicenseToDevice(license);
                });
            })
            .catch((err) => console.error(err));
    }

    // If isFirmwareUpdateCollapsed is toggled a number of times in a row, set the allowLocalFile to true
    private collapsedCount = 0;
    private isFirmwareUpdateCollapsedChanged() {
        let currentDateTime = new Date().getTime();
        const sixSeconds = 6000;

        // Used a timer so the user cannot unintentionally toggle this. They need to spam the drop down in 6 seconds
        if (currentDateTime - this.allowLocalFileTime <= sixSeconds) {
            this.collapsedCount++;
        } else {
            this.allowLocalFileTime = new Date().getTime();
            this.collapsedCount = 0;
            this.allowLocalFile = false;
        }

        if (this.collapsedCount >= 5) {
            this.allowLocalFile = true;
        }
    }

    //#endregion
}
